'use client';

import { passkey } from '@repo/auth';
import { useCallback, useEffect, useState } from 'react';
import { setPasskeyFlag } from '../../login/set-passkey-flag';
import { generatePasskeyName, getErrorMessage, isCancelledError } from './helper';
import type { IPasskeyStatus, IUsePasskeyStatusReturn } from './i-passkey-status';

export function usePasskeyStatus(): IUsePasskeyStatusReturn {
  const [state, setState] = useState<IPasskeyStatus>({
    hasPasskey: false,
    passkeyId: null,
    isLoading: true,
    error: null,
  });

  const refreshStatus = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await passkey.listUserPasskeys();

      if (result.error) {
        throw new Error(result.error.message);
      }

      const passkeys = result.data || [];
      const firstPasskey = passkeys[0];
      const hasPasskey = firstPasskey !== undefined;
      const passkeyId = firstPasskey?.id ?? null;

      setPasskeyFlag(hasPasskey);
      setState({ hasPasskey, passkeyId, isLoading: false, error: null });
    } catch (err) {
      const error = getErrorMessage(err, 'Failed to load passkey status');
      setState((prev) => ({ ...prev, isLoading: false, error }));
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const addPasskey = async (): Promise<void> => {
    setState((prev) => ({ ...prev, error: null }));

    try {
      const result = await passkey.addPasskey({ name: generatePasskeyName() });

      if (result.error) {
        throw new Error(result.error.message || 'Failed to add passkey');
      }

      await refreshStatus();
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to add passkey');

      if (!isCancelledError(message)) {
        setState((prev) => ({ ...prev, error: message }));
        throw err;
      }
    }
  };

  const removePasskey = async (): Promise<void> => {
    if (!state.passkeyId) {
      return;
    }

    setState((prev) => ({ ...prev, error: null }));

    try {
      const result = await passkey.deletePasskey({ id: state.passkeyId });

      if (result.error) {
        throw new Error(result.error.message || 'Failed to remove passkey');
      }

      await refreshStatus();
    } catch (err) {
      const error = getErrorMessage(err, 'Failed to remove passkey');
      setState((prev) => ({ ...prev, error }));
      throw err;
    }
  };

  return { ...state, addPasskey, removePasskey, refreshStatus };
}
